import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { PageHero, usePageMeta } from "@/components/site/PageHero";
import { CtaBand } from "@/components/site/CtaBand";
import { Levels } from "@/components/site/Levels";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { SERVICES, SERVICE_LABEL, bookingHref, serviceHref, waLink } from "@/lib/site";
import { SERVICE_DETAILS } from "@/lib/content";
import NotFound from "./NotFound";
import { KidsPage } from "@/components/site/KidsPage";

const Facts = ({ facts }) => (
  <div className="grid grid-cols-2 border-l border-t border-white/10 md:grid-cols-4">
    {facts.map((f, i) => (
      <div key={f.k} data-testid={`service-fact-${i}`} className="border-b border-r border-white/10 p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">{f.k}</p>
        <p className="mt-2 font-display text-2xl uppercase leading-tight text-white md:text-3xl">{f.v}</p>
      </div>
    ))}
  </div>
);

const Cards = ({ items, numbered }) => (
  <div className="grid grid-cols-1 border-l border-t border-white/10 md:grid-cols-3">
    {items.map((p, i) => (
      <Reveal key={p.t} delay={i * 0.08} className="border-b border-r border-white/10 p-8">
        <span className="flex h-10 w-10 items-center justify-center bg-brand font-display text-xl text-white">{numbered ? i + 1 : <Check className="h-5 w-5" />}</span>
        <h3 className="mt-6 font-display text-3xl uppercase leading-tight text-white">{p.t}</h3>
        <p className="mt-3 leading-relaxed text-neutral-400">{p.d}</p>
      </Reveal>
    ))}
  </div>
);

export default function ServicePage() {
  const { slug } = useParams();
  const d = SERVICE_DETAILS[slug];
  const name = SERVICE_LABEL[slug];
  usePageMeta(name && `${name} Çekmeköy Ömerli`, d?.intro);
  if (!d) return <NotFound />;
  if (slug === "enduro-tur") return <Navigate to="/enduro-tur" replace />;
  if (slug === "cocuk-egitim") return <KidsPage />;
  const others = SERVICES.filter((s) => s.key !== slug);
  return (
    <>
      <PageHero eyebrow={d.eyebrow} title={name} intro={d.intro} img={d.hero} crumbs={[{ label: "Eğitimler", to: "/egitimlerimiz" }, { label: name }]} />
      <section className="mx-auto max-w-7xl space-y-16 px-5 py-24 md:px-8 md:py-28">
        <Reveal className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <h2 className="max-w-2xl font-display text-5xl uppercase leading-none text-white md:text-7xl">{d.headline}</h2>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to={bookingHref(slug)} data-testid="service-booking-btn" className="group flex items-center justify-center gap-3 bg-brand px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand">Rezervasyon <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></Link>
            <a href={waLink(`Merhaba FX Motor Sporları, ${name} hakkında bilgi almak istiyorum.`)} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-btn" className="flex items-center justify-center gap-3 border border-white/30 px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black"><MessageCircle className="h-5 w-5" /> Bilgi al</a>
          </div>
        </Reveal>
        {d.facts && <Facts facts={d.facts} />}
        {d.steps && <div><Eyebrow>Nasıl işler?</Eyebrow><Cards items={d.steps} numbered /></div>}
        {d.points && <Cards items={d.points} />}
        {slug === "egitim" && <Levels />}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {d.images.map((src) => <img key={src} src={src} alt={`${name} — FX Motor Sporları`} loading="lazy" className="aspect-[4/5] w-full border border-white/10 object-cover" />)}
        </div>
      </section>
      <section className="border-t border-white/10 bg-surface">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8">
          <Eyebrow>Diğer eğitimler</Eyebrow>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {others.map((s) => (
              <Link key={s.key} to={serviceHref(s.key)} data-testid={`other-service-${s.key}`} className="group relative block aspect-[4/3] overflow-hidden border border-white/10">
                <img src={s.img} alt={s.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <span className="absolute bottom-4 left-4 font-display text-2xl uppercase text-white md:text-3xl">{s.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CtaBand service={slug} title={`${name} için yerini ayırt.`} />
    </>
  );
}
